import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirmPassword) return setError('Passwords do not match');
    if (!token) return setError('Invalid reset link. Please request a new one.');

    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        toast.success('Password reset successfully!');
      } else {
        setError(data.detail || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token && !success) {
    return (
      <div className="min-h-screen flex items-center justify-center auth-background" data-testid="reset-password-page">
        <Card className="w-full max-w-md glassmorphism border-zinc-800">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Invalid Reset Link</h2>
            <p className="text-zinc-400 mb-6">This link is missing or malformed.</p>
            <Link to="/forgot-password">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-black">Request New Link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center auth-background" data-testid="reset-password-page">
      <Card className="w-full max-w-md glassmorphism border-zinc-800">
        <CardHeader className="space-y-3 text-center">
          <div className="flex justify-center mb-2">
            <img
              src="https://res.cloudinary.com/dzlc6qta0/image/upload/v1771451035/d2e1bd_3ffc4a4bc88e4587b369a584fd8a636b_mv2_pvlple.png"
              alt="iEvntür Logo"
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {success ? 'Password Reset!' : 'Set New Password'}
          </CardTitle>
          <CardDescription className="text-zinc-400">
            {success ? 'You can now sign in with your new password.' : 'Choose a strong password for your account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
                data-testid="go-to-login-btn"
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400 flex items-center gap-2" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-white">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="new-password-input"
                    className="h-11 pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-white">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Re-enter password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                    className="h-11 pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500 focus:border-cyan-500 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-cyan-500 hover:bg-cyan-600 text-black font-medium"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
